import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { createOrder } from "../Redux/Actions/OrderActions";
import { ORDER_CREATE_RESET } from "../Redux/Constants/OrderConstants";
import Header from "./../components/Header";
import Message from "./../components/LoadingError/Error";
import { calculateShippingCost } from "../components/ShippingCost"; // Import the calculation function
import axios from "axios";

const PlaceOrderScreen = ({ history }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); 

  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  // Get query parameters to determine if this is a custom order
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isCustomOrder = searchParams.get("orderType") === "custom";
  const productId = searchParams.get("productId");

  // State for custom order fields
  const [size, setSize] = useState("A4");
  const [isFramed, setIsFramed] = useState(false);
  // const [isColored, setIsColored] = useState(true);
  const [specialNotes, setSpecialNotes] = useState("");
  const [customProduct, setCustomProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch product details for custom order with improved handling
  useEffect(() => {
    if (isCustomOrder && productId) {
      const fetchProductDetails = async () => {
        try {
          setLoading(true);
          
          // IMPROVED: First check localStorage for cached product details
          const cachedProductDetails = localStorage.getItem(`customProduct_${productId}`);
          if (cachedProductDetails) {
            setCustomProduct(JSON.parse(cachedProductDetails));
            setLoading(false);
            return;
          }
          
          // Then, check if the product is already in the cart
          const relatedProduct = cart.cartItems.find(item => item.product === productId);
          
          if (relatedProduct) {
            setCustomProduct(relatedProduct);
            // Store in localStorage for future reference
            localStorage.setItem(`customProduct_${productId}`, JSON.stringify(relatedProduct));
          } else {
            // If not in cart, fetch the product details from API
            const { data } = await axios.get(`/api/products/${productId}`);
            const productData = {
              product: data._id,
              name: data.name,
              image: data.image,
              price: data.price
            };
            setCustomProduct(productData);
            // Store in localStorage for future reference
            localStorage.setItem(`customProduct_${productId}`, JSON.stringify(productData));
          }
          setLoading(false);
        } catch (err) {
          console.error("Error fetching product details:", err);
          setError("Failed to fetch product details");
          setLoading(false);
        }
      };
      
      fetchProductDetails();
    }
  }, [isCustomOrder, productId, cart.cartItems]);

  // Calculate Price
  const addDecimals = (num) => {
    return (Math.round(num * 100) / 100).toFixed(2);
  };

  // Calculate items price
  cart.itemsPrice = addDecimals(
    cart.cartItems.reduce((acc, item) => acc + item.price * item.qty, 0)
  );
  
  // Calculate shipping using the ShippingCost component - no free shipping
  const itemsTotalValue = Number(cart.itemsPrice);
  cart.shippingPrice = addDecimals(
    calculateShippingCost(cart.shippingAddress.country, itemsTotalValue)
  );
  
  // Calculate tax based on shipping destination
  cart.taxPrice = addDecimals(
    cart.shippingAddress.country === "Kenya" 
      ? Number((0.16 * cart.itemsPrice).toFixed(2))  // 16% tax for Kenya
      : 0  // 0% tax for international shipments
  );
  
  cart.totalPrice = (
    Number(cart.itemsPrice) +
    Number(cart.shippingPrice) +
    Number(cart.taxPrice)
  ).toFixed(2);

  const orderCreate = useSelector((state) => state.orderCreate);
  const { order, success, orderError } = orderCreate;

  // Handle success for both custom and regular orders
  useEffect(() => {
    if (success) {
      history.push(`/order/${order._id}`);
      dispatch({ type: ORDER_CREATE_RESET });
    }
  }, [history, dispatch, success, order]);

  // Function to create a well-formatted WhatsApp message
  const createWhatsAppMessage = () => {
    let message = "Hello! I'd like to request a custom piece: \n\n";

    if (isCustomOrder && customProduct) {
        message += `Reference Art: ${customProduct.name}\n`;
        message += "Customization Details:\n";
        message += `  - Size: ${size}\n`;
        message += `  - Frame: ${isFramed ? 'Framed' : 'Unframed'}\n`;
        // message += `  - Color: ${isColored ? 'Colored' : 'Black & White'}\n`;
        if (specialNotes) {
            message += `  - Special Notes: ${specialNotes}\n\n`;
        }
        message += "\nSHIPPING DETAILS\n";
    message += `  - Name: ${userInfo.name}\n`;
    message += `  - Email: ${userInfo.email}\n`;
    message += `  - Address: ${cart.shippingAddress.address}, ${cart.shippingAddress.city}, ${cart.shippingAddress.postalCode}, ${cart.shippingAddress.country}\n`;
    message += `  - Shipping Cost: Ksh ${cart.shippingPrice}\n`;
    } else {
        message += "ORDER ITEMS\n";
        cart.cartItems.forEach((item, index) => {
            message += `  ${index + 1}. ${item.name}\n`;
            message += `     - Quantity: ${item.qty}\n`;
            message += `     - Price: Ksh ${item.price * item.qty}\n\n`;
            message += "\nSHIPPING DETAILS\n";
    message += `  - Name: ${userInfo.name}\n`;
    message += `  - Email: ${userInfo.email}\n`;
    message += `  - Address: ${cart.shippingAddress.address}, ${cart.shippingAddress.city}, ${cart.shippingAddress.postalCode}, ${cart.shippingAddress.country}\n`;
    message += `  - Shipping Cost: Ksh ${cart.shippingPrice}\n`;
    message += `  - Total Cost: Ksh ${cart.totalPrice}\n`;
        });
    }

    return encodeURIComponent(message);
  };

  // Function to handle order placement and WhatsApp redirection
  const placeOrderHandler = () => {
    const whatsappNumber = "+254746003525";
    const message = createWhatsAppMessage();

    // Check if the user is on a mobile device
    const isMobile = /iPhone|Android/i.test(navigator.userAgent);
    const whatsappURL = isMobile
        ? `https://wa.me/${whatsappNumber}?text=${message}`
        : `https://web.whatsapp.com/send?phone=${whatsappNumber}&text=${message}`;

    if (isCustomOrder && customProduct) {
        // Create custom order in database first
        const customOrderData = {
            orderItems: [{
                product: customProduct.product,
                name: customProduct.name,
                image: customProduct.image,
                price: customProduct.price,
                qty: 1
            }],
            shippingAddress: cart.shippingAddress,
            paymentMethod: "WhatsApp",
            itemsPrice: customProduct.price,
            shippingPrice: cart.shippingPrice,
            taxPrice: cart.taxPrice || 0,
            totalPrice: Number(customProduct.price) + Number(cart.shippingPrice) + Number(cart.taxPrice || 0),
            isCustomOrder: true,
            customDetails: {
                size,
                isFramed,
                specialNotes
            }
        };
        
        console.log("Sending custom order data:", customOrderData);
        dispatch(createOrder(customOrderData));
        
        // Redirect to WhatsApp based on device type
        window.open(whatsappURL, "_blank");
    } else {
        // Create the order in the database before redirection
        const standardOrderData = {
            orderItems: cart.cartItems,
            shippingAddress: cart.shippingAddress,
            paymentMethod: cart.paymentMethod || "WhatsApp",
            itemsPrice: cart.itemsPrice,
            shippingPrice: cart.shippingPrice,
            taxPrice: cart.taxPrice,
            totalPrice: cart.totalPrice,
            isCustomOrder: false
        };
        
        console.log("Sending standard order data:", standardOrderData);
        dispatch(createOrder(standardOrderData));

        // Redirect to WhatsApp after order creation
        window.open(whatsappURL, "_blank");
    }
  };

  return (
    <>
      <Header />
      <div className="page-title-container">
      <div className="placeorder-container">
        <div className="row order-detail">
          <div className="col-lg-4 col-sm-4 mb-lg-4 mb-5 mb-sm-0">
            <div className="row ">
              <div className="col-md-4 center">
                <div className="alert-success order-box">
                  <i className="fas fa-user"></i>
                </div>
              </div>
              <div className="col-md-8 center">
                <h5>
                  <strong>Customer</strong>
                </h5>
                <p>{userInfo.name}</p>
                <p>{userInfo.email}</p>
              </div>
            </div>
          </div>
          {/* 2 */}
          <div className="col-lg-4 col-sm-4 mb-lg-4 mb-5 mb-sm-0">
            <div className="row">
              <div className="col-md-4 center">
                <div className="alert-success order-box">
                  <i className="fas fa-truck-moving"></i>
                </div>
              </div>
              <div className="col-md-8 center">
                <h5>
                  <strong>Order info</strong>
                </h5>
                <p>Shipping: {cart.shippingAddress.country}</p>
                {/* Display shipping cost information */}
                <p className="shipping-cost">
                  Shipping Cost: Ksh {cart.shippingPrice}
                </p>
              </div>
            </div>
          </div>
          {/* 3 */}
          <div className="col-lg-4 col-sm-4 mb-lg-4 mb-5 mb-sm-0">
            <div className="row">
              <div className="col-md-4 center">
                <div className="alert-success order-box">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
              </div>
              <div className="col-md-8 center">
                <h5>
                  <strong>Deliver to</strong>
                </h5>
                <p>
                  Address: {cart.shippingAddress.city},{" "}
                  {cart.shippingAddress.address},{" "}
                  {cart.shippingAddress.postalCode}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Order Form */}
        {isCustomOrder && (
          <div className="custom-order-form row">
            {loading ? (
              <div className="col-12 text-center">
                <p>Loading product details...</p>
              </div>
            ) : error ? (
              <div className="col-12">
                <Message variant="alert-danger">{error}</Message>
              </div>
            ) : customProduct ? (
              <>
                <div className="col-lg-4">
                  <div className="custom-product-info">
                    <h4>Reference Art</h4>
                    {customProduct.image && (
                      <div className="custom-product-image">
                        <img src={customProduct.image} alt={customProduct.name} />
                      </div>
                    )}
                    <h5>{customProduct.name}</h5>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div className="custom-options">
                  <h4>Customization Options</h4>
                  <p className="subheading" style={{ color: '#6c757d' }}>Select the options below to customize your artwork.</p>
                    <div className="form-group">
                      <label>Size</label>
                      <select className="form-control" value={size} onChange={(e) => setSize(e.target.value)}>
                        <option value="A4">A4</option>
                        <option value="A3">A3</option>
                        <option value="A2">A2</option>
                        <option value="A1">A1</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Framing Options</label>
                      <select className="form-control" value={isFramed ? 'Framed' : 'Unframed'} onChange={(e) => setIsFramed(e.target.value === 'Framed')}>
                        <option value="Unframed">Unframed</option>
                        <option value="Framed">Framed</option>
                      </select>
                    </div>
                    
                    {/* <div className="form-group">
                      <label>Color Options</label>
                      <select className="form-control" value={isColored ? 'Colored' : 'Black & White'} onChange={(e) => setIsColored(e.target.value === 'Colored')}>
                        <option value="Colored">Colored</option>
                        <option value="Black & White">Black & White</option>
                      </select>
                    </div> */}
                  
                    <div className="form-group">
                      <label>Special Notes/Modifications</label>
                      <textarea
                        className="form-control"
                        value={specialNotes}
                        onChange={(e) => setSpecialNotes(e.target.value)}
                        placeholder="Add any special instructions or requests here..."
                      />
                    </div>
                  </div>
                </div>
                
                <div className="col-lg-4">
                  <div className="custom-order-summary">
                    <h4>Custom Order Summary</h4>
                    <ul>
                      <li><span>Reference Art</span><span>{customProduct.name}</span></li>
                      <li><span>Size</span><span>{size}</span></li>
                      <li><span>Framing</span><span>{isFramed ? 'Framed' : 'Unframed'}</span></li>
                      {/* <li><span>Color</span><span>{isColored ? 'Colored' : 'Black & White'}</span></li> */}
                      {specialNotes && <li><span>Special Notes</span><span>{specialNotes}</span></li>}
                      <li><span>Shipping</span><span>Ksh {cart.shippingPrice}</span></li>
                      <li><span>Total Price</span><span>Ksh {Number(customProduct.price) + Number(cart.shippingPrice) + Number(cart.taxPrice || 0)}</span></li>
                    </ul>
                    <button className="place-custom-order-btn" onClick={placeOrderHandler}>Place Order</button>
                  </div>
                </div>
              </>
            ) : (
              <div className="col-12 text-center">
                <p>Product not found. Please select a valid product for customization.</p>
              </div>
            )}
          </div>
        )}

        {/* Regular Order Items */}
        {!isCustomOrder && (
          <div className="row order-products justify-content-between">
            <div className="col-lg-8">
              {cart.cartItems.length === 0 ? (
                <Message variant="alert-info mt-5">Your cart is empty</Message>
              ) : (
                <>
                  {cart.cartItems.map((item, index) => (
                    <div className="order-product row" key={index}>
                      <div className="col-md-3 col-6">
                        <img src={item.image} alt={item.name} />
                      </div>
                      <div className="col-md-5 col-6 d-flex align-items-center">
                        <Link to={`/products/${item.product}`}>
                          <h6>{item.name}</h6>
                        </Link>
                      </div>
                      <div className="mt-3 mt-md-0 col-md-2 col-6  d-flex align-items-center flex-column justify-content-center ">
                        <h4>QUANTITY</h4>
                        <h6>{item.qty}</h6>
                      </div>
                      <div className="mt-3 mt-md-0 col-md-2 col-6 align-items-end  d-flex flex-column justify-content-center ">
                        <h4>SUBTOTAL</h4>
                        <h6>Ksh {item.qty * item.price}</h6>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
            {/* total */}
            <div className="col-lg-3 d-flex align-items-end flex-column mt-5 subtotal-order">
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <td>
                      <strong>Products</strong>
                    </td>
                    <td>Ksh {cart.itemsPrice}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Shipping</strong>
                    </td>
                    <td>Ksh {cart.shippingPrice}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Tax</strong>
                    </td>
                    <td>Ksh {cart.taxPrice}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Total</strong>
                    </td>
                    <td>Ksh {cart.totalPrice}</td>
                  </tr>
                </tbody>
              </table>
              {cart.cartItems.length === 0 ? null : (
                <button type="button" onClick={placeOrderHandler}>
                  PLACE ORDER
                </button>
              )}
              {orderError && (
                <div className="my-3 col-12">
                  <Message variant="alert-danger">{orderError}</Message>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default PlaceOrderScreen;